import { useState, useMemo } from 'react'
import { Card } from '@/components/ui/Card'
import { StatusBadge } from '@/components/ui/StatusBadge'
import type { WorkerInfo } from '@/api/dashboard'

const PAGE_SIZE = 10

function formatAge(seconds?: number): string {
  if (!seconds) return 'N/A'
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
}

interface Props {
  workers: WorkerInfo[]
}

export function WorkersTable({ workers }: Props) {
  const [page, setPage] = useState(0)

  const totalPages = Math.ceil(workers.length / PAGE_SIZE)
  const pageItems = useMemo(
    () => workers.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [workers, page],
  )

  // Reset to first page if workers list shrinks below current page
  if (page > 0 && page >= totalPages) {
    setPage(0)
  }

  return (
    <Card>
      <div style={{ overflowX: 'auto' }}>
        <table className="dashboard-table">
          <thead>
            <tr>
              <th>Worker ID</th>
              <th>Session ID</th>
              <th>State</th>
              <th>Language</th>
              <th>Avatar Ready</th>
              <th>Container</th>
              <th>Age</th>
              <th>Room</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', opacity: 0.5 }}>
                  No workers
                </td>
              </tr>
            ) : (
              pageItems.map((w) => (
                <tr key={w.worker_id}>
                  <td className="font-mono">{w.worker_id}</td>
                  <td className="font-mono">{w.session_id ?? '—'}</td>
                  <td><StatusBadge status={w.state} variant="worker" /></td>
                  <td>{w.language?.toUpperCase()}</td>
                  <td>{w.avatar_ready ? 'Yes' : 'No'}</td>
                  <td>{w.container_status}</td>
                  <td>{formatAge(w.age_seconds)}</td>
                  <td>{w.room_name ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <button
          className="btn btn--sm btn--outline"
          disabled={page === 0}
          onClick={() => setPage(page - 1)}
        >
          Previous
        </button>
        <span className="pagination-info">
          Page {page + 1} of {totalPages || 1} — {workers.length} total
        </span>
        <button
          className="btn btn--sm btn--outline"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage(page + 1)}
        >
          Next
        </button>
      </div>
    </Card>
  )
}